/* eslint-disable import/no-unused-modules */

export const PAYMENT_PROCESS_TIMEOUT_MESSAGE =
    "We couldn't connect to the payment service. Please try again.";
export const PAYMENT_SUCCESS_MESSAGE = 'Payment completed successfully!';

export type Currency = 'NZD' | 'USD' | 'AUD'; // Only supported currencies
export const CurrencyValues = ['NZD', 'USD', 'AUD'];

export type InvoiceStatus =
    | 'Draft'
    | 'Sent'
    | 'Expired'
    | 'Cancelled'
    | 'Accepted'
    | 'Issued'
    | 'PartialPaid'
    | 'Paid'
    | 'Overdue'
    | 'Refunded';

export type InvoicePaymentStatus =
    | 'Upcoming'
    | 'Overdue'
    | 'PartialPaid'
    | 'FullyPaid'
    | 'Canceled';

/**
 * Interface for product items in ecommerce
 */
export interface ProductItem {
    /** Unique identifier for the product */
    id: number;
    /** Product name */
    name: string;
    /** Detailed product description */
    description: string;
    /** URL to product image */
    imageUrl: string;
    /** Quantity */
    quantity: number;
    /** Original unit price in cents, before tax */
    unitPrice: number;
    /** Discounted price in cents (0 if no discount), before tax */
    discountedUnitPrice: number;
    /** Whether the product has a discount */
    discountApplied: boolean;
    /** Tax Rate, eg 0.15 for 15% */
    taxRate: number;
    /** Final price after discounts and tax in cents */
    totalPrice: number;
    currency: Currency;
}

/**
 * Interface for payment items in ecommerce
 */
export interface PaymentItem {
    /** Unique identifier for the payment */
    id: number;
    /** Payment name/description */
    name: string;
    /** Payment status */
    status: InvoicePaymentStatus;
    /** Timestamp of due date */
    dueDate: number | null;
    /** Timestamp when payment was made (null if not paid) */
    paymentDate: number | null;
    /** Payment amount in cents */
    amount: number;
    /** Payment currency */
    currency: Currency;
}

export interface ProfessionalInfo {
    title: string;
    subTitle: string;
    address: string;
    city: string;
    postCode: string;
    state: string;
    bn: string; // ABN or NZBN
}

export interface Contact {
    id: number;
    fullName: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
    postCode?: string;
    city?: string;
    hasStripePaymentMethod?: boolean;
}

export interface ProposalTemplate {
    headline: string;
    subHeadline: string;
    footerLinks: { title: string; url: string }[];
    note: string;
    contact: Contact;
    accountDetails?: string;
    scope?: string;
    paymentTerms?: string;
    creditCardTerms?: string;
    bankTransferTerms?: string;
    references?: string;
    /** Business terms from BusinessTermsVersion (title, body content, and URL) */
    businessTerms: { title: string; body: string; url: string }[];
}

export interface Proposal {
    id: number;
    urlSegment: string;
    uuid: string;
    docType: 'quote' | 'invoice';
    isSubscription: boolean;
    professionalInfo: ProfessionalInfo;
    proposalTemplate: ProposalTemplate;
    productItems: ProductItem[];
    paymentItems: PaymentItem[];
    status: InvoiceStatus;
    currency: Currency;
    discounts: number;
    subtotal: number;
    tax: number;
    total: number;
    summaries: string[];
    customer: Contact;
    expiryDate: number | null;
    invoiceSentDate: number | null;
    createdAt: number | null;
    updatedAt: number | null;
    stripeHostedInvoiceUrl: string | null;
    stripeAccount: string | null;
    /** Stripe publishable key for this invoice's Stripe account */
    publishableKey: string | null;
    hasStripePaymentMethod: boolean;
    /** Whether the invoice has a payment currently being processed */
    hasProcessingPayment: boolean;
    poNumber?: string;
    startDate?: string;
    endDate?: string;
    /** Signature data from when the quote was accepted */
    signature?: {
        fullName: string;
        email: string;
        dataUrl: string;
        signedDate: string;
    };
}

export interface SurchargeConfig {
    /** Surcharge rate as decimal (e.g. 0.027 for 2.7%) */
    rate: number;
    flatFee: number;
}
